import { useCallback } from 'react';
import { useApolloClient, DocumentNode } from '@apollo/client';

interface ListReorderHookInput<TQueryResult, TQueryVariables> {
  query: DocumentNode;
  queryVariables?: TQueryVariables;
  queryName: keyof TQueryResult;

  onCallMutation?: (from: number, to: number) => void;
}

interface ListReorderHookOutput {
  onReorderItems: (from: number, to: number) => void;
}

function useListReorder<TQueryResult, TQueryVariables = any>(input: ListReorderHookInput<TQueryResult, TQueryVariables>): ListReorderHookOutput {
  const apolloClient = useApolloClient();
  const { query, queryVariables, queryName, onCallMutation } = input;

  const onReorderItems = useCallback(
    (from: number, to: number) => {
      const cacheData = apolloClient.cache.readQuery<TQueryResult, TQueryVariables>({
        query,
        variables: queryVariables,
      });
      const items = cacheData?.[queryName] as any;
      if (!items) return;

      const result = Array.from(items);
      const [removed] = result.splice(from, 1);
      result.splice(to, 0, removed);

      const data: any = { [queryName]: result };
      apolloClient.writeQuery({
        query,
        variables: queryVariables,
        data,
      });
      // eslint-disable-next-line no-unused-expressions
      onCallMutation?.(from, to);
    },
    [apolloClient, onCallMutation, query, queryVariables, queryName]
  );

  return {
    onReorderItems,
  };
}

export default useListReorder;
